let _ = t => t,
  _t;
import { useMemo } from 'react';
import { RouterProvider } from 'quartz-routing';
import { Routes } from './components/Routes';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { GET_TICKET_THREAD_QUERY_POLICY } from './data/useTicketThread';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { usePortalData } from './data/usePortalData';
import I18n from 'I18n';
import { AccessDenied } from './components/AccessDenied';
import { CenteredSpinner } from './components/CenteredSpinner';
import { GlobalNav } from './components/GlobalNav';
import { useEffect } from 'react';
import { getPortalBasename } from 'portals-platform-lib';
import { TicketCreationProvider } from './components/TicketCreationProvider';
import { NavigationProvider } from './hooks/useNavigationContext';

// @bend-expect quartz-no-create-browser-history
import { createBrowserHistory } from 'history';
import { Metrics } from './utils/metrics';
import { useFormData } from './data/useFormData';
import { useLoginVerify } from './data/useLoginVerify';
import { getPortalData } from './data/usePortalData';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { initializePortal } from 'portals-platform-lib';
import { PortalPlatformDataContext } from './hooks/usePortalPlatformData';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { createGlobalStyle } from 'styled-components';
import { usageTracker } from './utils/usageTracker';
import { getIsPreview } from './utils/isPreview';
import { PreviewProvider } from './hooks/usePreviewContext';
import { ListingsSortAndFiltersProvider } from './hooks/useListingsSortAndFiltersContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Note: This currently duplicates the usePortalData fetch, but it's called in a different context, so we can't use the hook directly.
 * I propose we eventually refactor the endpoint to give us just a language endpoint.
 */
export const getLocaleInfo = async () => {
  try {
    const {
      language
    } = await getPortalData();
    return {
      locale: language,
      langEnabled: true,
      timezone: undefined,
      userObjectTimezone: undefined
    };
  } catch (error) {
    return {
      locale: navigator.language,
      langEnabled: false,
      timezone: undefined,
      userObjectTimezone: undefined
    };
  }
};
export const prepareApp = async () => {
  let portalPlatformData = null;
  try {
    const {
      portalData
    } = await initializePortal();
    portalPlatformData = portalData;
  } catch (error) {
    console.error('Failed to fetch brand and theme data:', error);
  }
  const client = new DataFetchingClient({
    queryFieldPolicies: Object.assign({}, GET_TICKET_THREAD_QUERY_POLICY)
  });
  return {
    props: {
      dataFetchingClient: client,
      portalPlatformData
    }
  };
};
export function AppComponent({
  dataFetchingClient,
  portalPlatformData
}) {
  return /*#__PURE__*/_jsx(DataFetchingClientProvider, {
    client: dataFetchingClient,
    children: /*#__PURE__*/_jsx(PortalPlatformDataContext.Provider, {
      value: portalPlatformData,
      children: /*#__PURE__*/_jsx(AppContent, {})
    })
  });
}
const GlobalUIPopoverStyles = createGlobalStyle(_t || (_t = _`
  [data-component-name="UIPopover"] {
    border-radius: ${0} !important;
  }
`), ({
  theme,
  isUsingPillBorderRadius
}) => isUsingPillBorderRadius ? theme.borderRadius[0] : theme.borderRadius[300]);
function AppContent() {
  var _portalData$data;
  const loginVerify = useLoginVerify();
  const portalData = usePortalData();
  const formData = useFormData();
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const isPreviewMode = getIsPreview().isPreview;
  useEffect(() => {
    usageTracker.setProperties({
      isEditing: isPreviewMode
    });
  }, [isPreviewMode]);
  useEffect(() => {
    if (portalData.data && portalData.data.settings) {
      document.title = portalData.data.settings.pageTitle;
    }
  }, [portalData.data]);
  const isLoading = portalData.loading || formData.loading || loginVerify.loading;
  const history = useMemo(() => createBrowserHistory({
    basename: document.location.hostname.includes('hubspotqa.com') ? 'customer-portal-app' : getPortalBasename()
  }), []);
  useEffect(() => {
    if (!isLoading) {
      Metrics.timer('startup').update(performance.now());
    }
  }, [isLoading]);
  if (loginVerify.isForbidden) {
    return /*#__PURE__*/_jsx(AccessDenied, {});
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(CenteredSpinner, {
      ariaLabel: I18n.text('customer-portal-app.loading')
    });
  }

  /**
   * This prevents the app from rendering if the portal data is blank,
   * this can occur if the portal data endpoint has loaded but a 403 was returned,
   * prior to the redirect taking effect.
   */
  if (!((_portalData$data = portalData.data) !== null && _portalData$data !== void 0 && _portalData$data.settings)) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GlobalUIPopoverStyles, {
      isUsingPillBorderRadius: isUsingPillBorderRadius
    }), /*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(RouterProvider, {
      history: history,
      children: /*#__PURE__*/_jsx(PreviewProvider, {
        children: /*#__PURE__*/_jsx(NavigationProvider, {
          children: /*#__PURE__*/_jsx(TicketCreationProvider, {
            children: /*#__PURE__*/_jsx(ListingsSortAndFiltersProvider, {
              children: /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                blockSize: "100dvh",
                children: [/*#__PURE__*/_jsx(GlobalNav, {}), /*#__PURE__*/_jsx(View, {
                  backgroundColor: "fill-surface-raised",
                  flex: "1 1 0",
                  overflowY: "auto",
                  children: /*#__PURE__*/_jsx(Routes, {})
                })]
              })
            })
          })
        })
      })
    })]
  });
}